import { MAX_FIGHTERS, type CharacterKind, type Difficulty } from '@/components'
import { ARENAS, ARENA_SIZES } from '@/game/arenas'
import { PLAYER_COLORS, WIN_TARGETS } from '@/game/resources'
import { BODY_PLANS, CHARACTER_KINDS } from '@/gfx/procedural/characterKinds'
import type { MemberView, RoomConfig, RoomMode, RoomView } from '@/net/protocol'
import { AI_PROFILES } from '@/systems/AiSystem'
import { el, row, wrap } from './Hud'

export interface RoomCallbacks {
  create(nick: string): void
  join(nick: string, code: string): void
  rename(nick: string): void
  back(): void
  pick(kind: CharacterKind): void
  watch(on: boolean): void
  configure(config: RoomConfig): void
  start(): void
  leave(): void
  abort(): void
  /** Closes the in-match menu; the match never stopped. */
  resume(): void
}

type Screen = 'entry' | 'room' | 'match' | 'results' | 'none'

const MODE_LABELS: Array<[RoomMode, string]> = [
  ['ffa', 'КАЖДЫЙ САМ ЗА СЕБЯ'],
  ['coop', 'ЛЮДИ ПРОТИВ БОТОВ'],
]

/**
 * The online screens: entering a room, the room itself, the menu over a
 * running match and the result card.
 *
 * A view only: it shows the room the server last described and reports
 * clicks, and never decides anything itself — the server owns every rule
 * (docs/ONLINE.md §7), and a refused click comes back as a message.
 */
export class RoomScreen {
  private readonly root: HTMLElement
  private readonly screens: Record<Exclude<Screen, 'none'>, HTMLElement>
  private current: Screen = 'none'

  private readonly nickInput: HTMLInputElement
  private readonly codeInput: HTMLInputElement
  private readonly entryMessage: HTMLElement

  private readonly codeLine: HTMLElement
  private readonly copyButton: HTMLElement
  private readonly roomNick: HTMLInputElement
  private readonly memberList: HTMLElement
  private readonly roomMessage: HTMLElement
  private readonly watchButton: HTMLElement
  private readonly startButton: HTMLElement
  private readonly startHint: HTMLElement
  private readonly modelButtons = new Map<CharacterKind, HTMLElement>()
  private readonly settingButtons: Array<{ el: HTMLElement; on(c: RoomConfig): boolean; set(c: RoomConfig): RoomConfig }> = []

  private readonly abortButton: HTMLElement
  private readonly winnerLine: HTMLElement
  private readonly scoreLine: HTMLElement

  private view: RoomView | null = null

  constructor(
    parent: HTMLElement,
    private readonly portraits: () => Record<CharacterKind, string>,
    private readonly callbacks: RoomCallbacks,
  ) {
    this.root = el('div', 'menu-root')
    this.root.style.position = 'absolute'
    this.root.style.inset = '0'
    this.root.style.pointerEvents = 'none'

    // ── entry ──
    const entry = el('div', 'screen hidden')
    const title = el('h1', 'title')
    title.style.fontSize = '46px'
    title.textContent = 'ИГРА ПО СЕТИ'
    const subtitle = el('div', 'subtitle')
    subtitle.textContent = 'КОМНАТА ПО ССЫЛКЕ · ДО ШЕСТИ БОЙЦОВ · ПУСТЫЕ МЕСТА ЗАЙМУТ БОТЫ'

    this.nickInput = input('online-input', 'НИК', 12)
    const nickRow = el('div', 'row')
    nickRow.append(label('НИК'), this.nickInput)

    const create = el('button', 'btn primary')
    create.textContent = 'СОЗДАТЬ КОМНАТУ'
    create.addEventListener('click', () => callbacks.create(this.nick()))

    this.codeInput = input('online-input code', 'КОД', 6)
    this.codeInput.addEventListener('input', () => {
      this.codeInput.value = this.codeInput.value.toUpperCase()
    })
    const join = el('button', 'btn')
    join.textContent = 'ВОЙТИ'
    join.addEventListener('click', () => callbacks.join(this.nick(), this.codeInput.value.trim()))
    const joinRow = el('div', 'row')
    joinRow.append(label('КОД КОМНАТЫ'), this.codeInput, join)

    this.entryMessage = el('div', 'online-message')
    const back = el('button', 'btn small')
    back.textContent = 'НАЗАД'
    back.addEventListener('click', () => callbacks.back())
    entry.append(title, subtitle, nickRow, create, joinRow, this.entryMessage, back)

    // ── room ──
    const room = el('div', 'screen hidden')
    const roomTitle = el('div', 'online-code-row')
    this.codeLine = el('h1', 'title online-code')
    this.copyButton = el('button', 'btn small')
    this.copyButton.textContent = 'СКОПИРОВАТЬ ССЫЛКУ'
    this.copyButton.addEventListener('click', () => this.copyLink())
    roomTitle.append(this.codeLine, this.copyButton)

    // Someone who came in by a link never saw the entry screen's nick field.
    this.roomNick = input('online-input', 'НИК', 12)
    this.roomNick.addEventListener('change', () => {
      const nick = this.roomNick.value.trim()
      if (nick) callbacks.rename(nick)
    })
    const nickLine = row(label('ТВОЙ НИК'), this.roomNick)

    const membersLabel = sectionLabel('В КОМНАТЕ')
    this.memberList = el('div', 'online-members')

    const pickerLabel = sectionLabel('ТВОЙ БОЕЦ')
    const picker = el('div', 'model-grid online-picker')
    for (const kind of CHARACTER_KINDS) {
      const btn = el('button', 'model-option clickable')
      const image = document.createElement('img')
      image.className = 'model-portrait'
      image.alt = BODY_PLANS[kind].name
      const name = el('div', 'model-name')
      name.textContent = BODY_PLANS[kind].name
      btn.append(image, name)
      btn.title = BODY_PLANS[kind].blurb
      btn.addEventListener('click', () => callbacks.pick(kind))
      picker.append(btn)
      this.modelButtons.set(kind, btn)
    }
    this.watchButton = el('button', 'btn small toggle')
    this.watchButton.textContent = 'ТОЛЬКО СМОТРЕТЬ'
    this.watchButton.addEventListener('click', () => {
      callbacks.watch(!(this.me()?.watching ?? false))
    })

    const settings = el('div', 'online-settings')
    settings.append(
      this.settingRow(
        'РЕЖИМ',
        MODE_LABELS.map(([mode, text]) => [text, (c) => c.mode === mode, (c) => ({ ...c, mode })]),
      ),
      this.settingRow(
        'БОЙЦОВ НА АРЕНЕ',
        [2, 3, 4, 5, 6].map((size) => [
          String(size),
          (c) => c.size === size,
          (c) => ({ ...c, size }),
        ]),
      ),
      this.settingRow(
        'БОТЫ',
        ([0, 1, 2] as Difficulty[]).map((level) => [
          AI_PROFILES[level].label,
          (c) => c.difficulty === level,
          (c) => ({ ...c, difficulty: level }),
        ]),
      ),
      this.settingRow(
        'АРЕНА',
        ARENAS.map((def) => [def.name, (c) => c.arenaId === def.id, (c) => ({ ...c, arenaId: def.id })]),
      ),
      this.settingRow(
        'ПЛОЩАДКА',
        ARENA_SIZES.map((size) => [
          size.label,
          (c) => c.arenaScale === size.scale,
          (c) => ({ ...c, arenaScale: size.scale }),
        ]),
      ),
      this.settingRow(
        'ПОБЕД ДО ВЫИГРЫША',
        WIN_TARGETS.map((wins) => [
          String(wins),
          (c) => c.winsNeeded === wins,
          (c) => ({ ...c, winsNeeded: wins }),
        ]),
      ),
    )

    this.startButton = el('button', 'btn primary online-start')
    this.startButton.textContent = 'НАЧАТЬ МАТЧ'
    this.startButton.addEventListener('click', () => callbacks.start())
    this.startHint = el('div', 'online-message')
    this.roomMessage = el('div', 'online-message')
    const leave = el('button', 'btn small')
    leave.textContent = 'ВЫЙТИ ИЗ КОМНАТЫ'
    leave.addEventListener('click', () => callbacks.leave())

    room.append(
      roomTitle,
      nickLine,
      wrap(membersLabel, this.memberList),
      wrap(pickerLabel, picker, row(this.watchButton)),
      settings,
      wrap(this.startButton, this.startHint),
      this.roomMessage,
      leave,
    )

    // ── the menu over a running match ──
    const match = el('div', 'screen hidden')
    const matchTitle = el('h1', 'title')
    matchTitle.style.fontSize = '40px'
    matchTitle.textContent = 'МАТЧ ИДЁТ'
    const matchNote = el('div', 'subtitle')
    matchNote.textContent = 'ПАУЗЫ ПО СЕТИ НЕТ · БОЙ ПРОДОЛЖАЕТСЯ'
    const resume = el('button', 'btn primary')
    resume.textContent = 'ВЕРНУТЬСЯ'
    resume.addEventListener('click', () => callbacks.resume())
    this.abortButton = el('button', 'btn')
    this.abortButton.textContent = 'ПРЕРВАТЬ МАТЧ'
    this.abortButton.addEventListener('click', () => callbacks.abort())
    const quit = el('button', 'btn')
    quit.textContent = 'ВЫЙТИ ИЗ КОМНАТЫ'
    quit.addEventListener('click', () => callbacks.leave())
    match.append(matchTitle, matchNote, row(resume, this.abortButton, quit))

    // ── results ──
    const results = el('div', 'screen hidden')
    this.winnerLine = el('div', 'winner-line')
    this.scoreLine = el('div', 'score-line')
    const returning = el('div', 'subtitle')
    returning.textContent = 'СЕЙЧАС ВСЕ ВЕРНУТСЯ В КОМНАТУ'
    results.append(this.winnerLine, this.scoreLine, returning)

    this.screens = { entry, room, match, results }
    this.root.append(entry, room, match, results)
    parent.append(this.root)
  }

  get shown(): Screen {
    return this.current
  }

  show(screen: Screen): void {
    this.current = screen
    for (const [name, node] of Object.entries(this.screens)) {
      node.classList.toggle('hidden', name !== screen)
    }
  }

  showEntry(nick: string, code: string, message = ''): void {
    this.nickInput.value = nick
    this.codeInput.value = code
    this.entryMessage.textContent = message
    this.show('entry')
  }

  /** Something went wrong or was refused; shown on whichever screen is up. */
  message(text: string): void {
    this.entryMessage.textContent = text
    this.roomMessage.textContent = text
  }

  showResults(winner: string, score: number[]): void {
    this.winnerLine.textContent = `ПОБЕДА · ${winner}`
    this.scoreLine.textContent = score.join(' : ')
    this.show('results')
  }

  showMatchMenu(): void {
    this.abortButton.style.display = this.isHost() ? '' : 'none'
    this.show('match')
  }

  /** Redraws the room from the server's latest description. */
  update(view: RoomView): void {
    this.view = view
    this.codeLine.textContent = `КОМНАТА ${view.code}`
    const me = this.me()
    const host = this.isHost()

    // Each member in the colour of their slot: the one they play in, or the one
    // the next match will give them, in the order it fills them.
    const next = view.members.filter((m) => !m.watching && m.connected)
    const rows = view.members.map((member) =>
      memberRow(member, member.slot ?? next.indexOf(member), view, me),
    )
    // The bots are not members, but the room should say how many there will be.
    const players = Math.min(next.length, MAX_FIGHTERS)
    const bots = Math.min(MAX_FIGHTERS, Math.max(view.config.size, players)) - players
    if (bots > 0) {
      const line = el('div', 'online-member online-bots')
      line.textContent = `+ ${bots} ${bots === 1 ? 'БОТ' : bots < 5 ? 'БОТА' : 'БОТОВ'}`
      rows.push(line)
    }
    this.memberList.replaceChildren(...rows)
    if (document.activeElement !== this.roomNick) this.roomNick.value = me?.nick ?? ''

    const portraits = this.portraits()
    for (const [kind, btn] of this.modelButtons) {
      btn.classList.toggle('selected', me?.model === kind)
      const image = btn.querySelector('img')
      if (image && image.src !== portraits[kind]) image.src = portraits[kind]
    }
    this.watchButton.classList.toggle('on', me?.watching ?? false)

    for (const setting of this.settingButtons) {
      setting.el.classList.toggle('on', setting.on(view.config))
      ;(setting.el as HTMLButtonElement).disabled = !host
    }

    this.startButton.style.display = host ? '' : 'none'
    ;(this.startButton as HTMLButtonElement).disabled = view.blocked !== null
    const hostNick = view.members.find((m) => m.id === view.host)?.nick ?? '—'
    this.startHint.textContent = host
      ? (view.blocked ?? '')
      : `НАСТРОЙКИ МЕНЯЕТ И МАТЧ НАЧИНАЕТ ХОСТ · ${hostNick}`
  }

  private me(): MemberView | undefined {
    return this.view?.members.find((m) => m.id === this.view!.you)
  }

  private isHost(): boolean {
    return this.view !== null && this.view.host === this.view.you
  }

  private nick(): string {
    return this.nickInput.value.trim()
  }

  private settingRow(
    title: string,
    options: Array<[string, (c: RoomConfig) => boolean, (c: RoomConfig) => RoomConfig]>,
  ): HTMLElement {
    const line = el('div', 'row')
    line.append(label(title))
    for (const [text, on, set] of options) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = text
      btn.addEventListener('click', () => {
        if (this.view) this.callbacks.configure(set(this.view.config))
      })
      line.append(btn)
      this.settingButtons.push({ el: btn, on, set })
    }
    return line
  }

  private copyLink(): void {
    if (!this.view) return
    const link = roomLink(this.view.code)
    // The clipboard exists only on a secure page — not on a LAN address over
    // plain http. Then the link is shown to copy by hand.
    if (!navigator.clipboard) {
      this.message(link)
      return
    }
    void navigator.clipboard.writeText(link).then(
      () => {
        this.copyButton.textContent = 'ССЫЛКА СКОПИРОВАНА'
        setTimeout(() => {
          this.copyButton.textContent = 'СКОПИРОВАТЬ ССЫЛКУ'
        }, 1500)
      },
      () => this.message(link),
    )
  }
}

/** The address a friend opens to walk straight into the room. */
export function roomLink(code: string): string {
  return `${location.origin}${location.pathname}#room=${code}`
}

function memberRow(
  member: MemberView,
  slot: number,
  view: RoomView,
  me: MemberView | undefined,
): HTMLElement {
  const line = el('div', 'online-member')
  const swatch = el('div', 'online-swatch')
  // Only members with a slot get its colour; spectators stay grey.
  if (slot >= 0 && slot < MAX_FIGHTERS) {
    swatch.style.background = `#${PLAYER_COLORS[slot].toString(16).padStart(6, '0')}`
  }
  const nick = el('div', 'online-nick')
  nick.textContent = member.nick
  const fighter = el('div', 'online-fighter')
  fighter.textContent = BODY_PLANS[member.model].name
  const badges = el('div', 'online-badges')
  const tags: string[] = []
  if (member.id === me?.id) tags.push('ТЫ')
  if (member.id === view.host) tags.push('ХОСТ')
  if (member.watching) tags.push('СМОТРИТ')
  if (!member.connected) tags.push('НЕТ СВЯЗИ')
  badges.textContent = tags.join(' · ')
  line.append(swatch, nick, fighter, badges)
  return line
}

function input(className: string, placeholder: string, maxLength: number): HTMLInputElement {
  const node = document.createElement('input')
  node.className = `${className} clickable`
  node.placeholder = placeholder
  node.maxLength = maxLength
  node.spellcheck = false
  return node
}

function label(text: string): HTMLElement {
  const node = el('div', 'inline-label')
  node.textContent = text
  return node
}

function sectionLabel(text: string): HTMLElement {
  const node = el('div', 'section-label')
  node.textContent = text
  return node
}
